import { pathOr } from 'ramda';
import React, { useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  Box,
  Button,
  Heading,
  OldTable,
  Text,
  Pagination,
} from '@frontend/components';

import { toCamelCase } from 'utils/stringUtils';

import { useSearchDataContext } from '../context';
import ComponentMatchList from './ComponentMatchList';
import MobileFiltersPopover from './MobileFiltersPopover';
import NoResults from './NoResults';
import TextMatchesCell from './TextMatchesCell';

const ResultList = React.forwardRef(function ResultList(
  { count, results, isActive, onSeeAll, label, children, ...rest },
  ref,
) {
  if (!isActive && count == 0) {
    return null;
  }

  const boxProps = isActive
    ? {}
    : {
        borderBottom: 'solid 1px',
        borderBottomColor: 'separator.divider',
      };

  return (
    <Box ref={ref} minWidth="800px" {...boxProps} {...rest}>
      {!isActive && (
        <Heading as="h3" size="lg" pt={2} pb={1}>
          {`${label} (${count})`}
          {count > results.length && (
            <Button variant="link" onClick={onSeeAll} size="md" pl={1} pr={1}>
              See All
            </Button>
          )}
        </Heading>
      )}
      {children}
    </Box>
  );
});

const noPadding = () => ({ style: { padding: 0 } });
const verticalAlignTop = () => ({ style: { verticalAlign: 'top' } });

const SearchResultList = ({
  data: rqData,
  aliases, // mapping from fields to aliases for those fields in text matches
  fsHide, // list of keys to redact data from full story
  tabKey,
  showComponentMatches, // whether to show component matches
  removeMatchCellPadding, // whether to remove match cell padding
  title, // Optional title for the table if different than labelPlural
  labelPlural, // The plural version of the index label
  labelSingular, // The singular version of the index label
  matchCell, // (result) => JSX
  highlightConfig: {
    alwaysVisibleFields, // string[], what fields are always visible in the match cell
    children, // Map<string, Array<object>>, mapping from names of child documents to highlight config objects (arrays of { field, key, label } objects)
    allowLinks, // string[], list of fields to allow links for. Use the "key" field to allow links for child highlights
  },
  filters, // Filters component
  paginationInfo, // { paginateFrom: number, paginateSize: number }
  onPaginate,
}) => {
  const { activeTab, setActiveTab } = useSearchDataContext();
  const { paginateFrom, paginateSize } = paginationInfo;

  const { results, count } = rqData ?? {};

  const key = toCamelCase(labelSingular);
  const isActive = activeTab == tabKey;

  const scrollDivRef = useRef(null);
  const tableRef = useRef(null);

  if (!isActive && count == 0) {
    return null;
  }

  const columns = [
    {
      title: labelSingular,
      key: key,
      width: showComponentMatches ? '33%' : '50%',
      dataIndex: key,
      onCell: removeMatchCellPadding ? noPadding : undefined,
    },
    ...(showComponentMatches
      ? [
          {
            title: 'Component Matches',
            key: 'componentMatches',
            width: '33%',
            dataIndex: 'componentMatches',
          },
        ]
      : []),
    { title: 'Text Matches', key: 'textMatches', dataIndex: 'textMatches' },
  ];

  const data = results.map((result) => ({
    [key]: matchCell(result),
    ...(showComponentMatches
      ? {
          componentMatches: (
            <ComponentMatchList partResult={pathOr({}, ['children'], result)} />
          ),
        }
      : {}),
    textMatches: (
      <TextMatchesCell
        aliases={aliases}
        fsHide={fsHide}
        excludeList={alwaysVisibleFields}
        result={result}
        childrenHighlightConfig={children}
        linkFields={allowLinks}
      />
    ),
  }));

  return (
    <Box
      width="100%"
      height={isActive ? '100%' : 'auto'}
      overflowX="auto"
      overflowY="hidden"
    >
      {data.length > 0 ? (
        <>
          {isActive && <MobileFiltersPopover>{filters}</MobileFiltersPopover>}
          <Box display="flex" height="100%">
            {isActive && (
              <Box display={{ base: 'none', tablet: 'flex' }} flexDir="column">
                <Box
                  bg="page.surface"
                  borderRight="solid 1px"
                  borderColor="separator.divider"
                  display="flex"
                  flex="1 0 0"
                  flexDir="column"
                  height="100%"
                  width={26}
                  padding={1}
                  paddingLeft={1.5}
                  overflow="auto"
                >
                  {filters}
                </Box>
              </Box>
            )}
            <Box
              display="flex"
              flex="1 1 0"
              flexDir="column"
              height="100%"
              minWidth="0"
            >
              <Box
                flex="1 0 auto"
                maxHeight="calc(100% - 40px)"
                overflowX="auto"
                ref={scrollDivRef}
              >
                <ResultList
                  count={count}
                  isActive={isActive}
                  label={title ?? labelPlural}
                  onSeeAll={() => setActiveTab(tabKey)}
                  paginateFrom={paginateFrom}
                  paginateSize={paginateSize}
                  ml={isActive ? 0 : 3}
                  mr={isActive ? 0 : 3}
                  pb={isActive ? 0 : 3}
                  results={results}
                >
                  <OldTable
                    columns={columns}
                    data={data}
                    tableLayout="fixed"
                    variant={isActive ? 'borderless' : 'default'}
                    onRow={verticalAlignTop}
                    height="100%"
                    ref={tableRef}
                  />
                </ResultList>
              </Box>
              {isActive && (
                <Box
                  display="flex"
                  flex="0 0 auto"
                  height={5}
                  justifyContent="flex-end"
                  flexDir="row"
                  borderTop="1px solid"
                  borderTopColor="separator.divider"
                >
                  <Text m="auto 0" color="feature.secondary">
                    {count > 0
                      ? `${paginateFrom + 1}-${Math.min(
                          paginateFrom + paginateSize,
                          count,
                        )} of ${count} ${
                          count === 1 ? labelSingular : labelPlural
                        }`
                      : `0 ${labelPlural}`}
                  </Text>
                  <Box m="auto 0" mr={0.5}>
                    <Pagination
                      currentPage={paginateFrom / paginateSize + 1}
                      itemsPerPage={paginateSize}
                      onChangePage={(page) => {
                        const curPage = paginateFrom / paginateSize + 1;
                        if (page !== curPage) {
                          onPaginate({
                            paginateFrom: (page - 1) * paginateSize,
                            paginateSize: paginateSize,
                          });
                          scrollDivRef.current.scroll(0, 0);
                        }
                      }}
                      totalItems={count}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <NoResults height="100vh" />
      )}
    </Box>
  );
};

const SearchResultListWrapper = (props) => (
  <ErrorBoundary
    FallbackComponent={() => null}
    onError={(e) => console.error(e)}
  >
    <SearchResultList {...props} />
  </ErrorBoundary>
);
export default SearchResultListWrapper;
